/**
 * Persian/Arabic text normalization (red-team R8) — used by imports so pasted Sepidar/Excel data
 * with Persian digits or Arabic letter variants matches our codes.
 */
const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';
/**
 * Convert Persian (۰-۹) and Arabic (٠-٩) digits to Latin (0-9), fold the U+2212 MINUS SIGN to ASCII
 * '-', and drop the U+200E/U+200F bidi marks.
 *
 * WHY the minus/bidi handling (WEB-04): `toLocaleString('fa-IR')` renders a negative as
 * U+200E (LRM) + U+2212 (MINUS SIGN) — NOT ASCII '-'. Both survived here and were then deleted by
 * downstream ASCII-only filters (NumberInput.sanitize) and by Number() (parseLooseNumber → NaN),
 * silently flipping a −10% bulk-indexation into +10%. Folding at this single authority fixes every
 * consumer at once. We deliberately do NOT fold U+2010/U+2013 or other unicode dashes: normalizeDigits
 * also underlies canonicalCode, where folding an en-dash would merge two distinct product codes.
 */
export function normalizeDigits(s) {
    let out = '';
    for (const ch of s) {
        const fa = FA_DIGITS.indexOf(ch);
        if (fa >= 0) {
            out += String(fa);
            continue;
        }
        const ar = AR_DIGITS.indexOf(ch);
        if (ar >= 0) {
            out += String(ar);
            continue;
        }
        if (ch === '−') {
            out += '-'; // MINUS SIGN → ASCII hyphen-minus
            continue;
        }
        if (ch === '‎' || ch === '‏')
            continue; // strip LRM/RLM bidi marks
        out += ch;
    }
    return out;
}
/** Unify Arabic letter variants with Persian (ك→ک, ي→ی), normalize digits, collapse whitespace. */
export function normalizeFa(s) {
    return normalizeDigits(s)
        .replace(/ك/g, 'ک')
        .replace(/ي/g, 'ی')
        .replace(/‌/g, ' ') // ZWNJ → space for matching
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Parse a number that may use Persian/Arabic digits, `,`/`٬` thousands separators, and the Arabic
 * decimal separator `٫`. NaN if invalid. The decimal separator is CONVERTED to `.` (not stripped) —
 * stripping it turned «۱٫۵» into 15, a silent 10× error on any decimal (e.g. an import discount).
 */
export function parseLooseNumber(s) {
    const cleaned = normalizeDigits(s)
        .replace(/٫/g, '.') // Arabic decimal separator → '.'
        .replace(/[,٬]/g, '') // thousands separators only
        .trim();
    return cleaned === '' ? NaN : Number(cleaned);
}
//# sourceMappingURL=normalize.js.map